import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { brand } from "../mock";
import { Menu, X, ArrowRight } from "lucide-react";

const navItems = [
  { id: "services", label: "Dienstleistungen" },
  { id: "angebote", label: "Angebote" },
  { id: "team", label: "Team" },
  { id: "kontakt", label: "Kontakt" },
];

export default function BrandHeader() {
  const [active, setActive] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const ids = navItems.map((n) => n.id);
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  // Body-Scroll sperren, wenn das mobile Menü offen ist
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const onNavClick = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const logoSrc = brand.logoUrl && !logoError ? brand.logoUrl : "/logo.png";

  return (
    <div
      className={`fixed left-1/2 z-50 -translate-x-1/2 w-[96%] max-w-6xl md:w-[94%] transition-all duration-300 ease-out ${
        scrolled ? "top-2.5" : "top-5"
      }`}
    >
      <div
        className={`mx-auto flex items-center gap-6 rounded-full border transition-all duration-300 ease-out backdrop-blur-xl ${
          scrolled
            ? "px-5 py-2 border-[#28D8FF]/25 bg-[#0A111F]/90 shadow-[0_12px_40px_rgba(2,6,23,0.45)]"
            : "px-6 py-2.5 border-white/10 bg-[#0A111F]/75 shadow-[0_8px_30px_rgba(2,6,23,0.30)]"
        }`}
      >
        <a
          className="shrink-0 select-none leading-none mr-4 md:mr-8"
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          data-testid="header-logo-link"
        >
          <img
            src={logoSrc}
            alt="l'azote logo"
            className={`h-10 w-auto brightness-0 invert transform origin-left transition-transform duration-300 ${
              scrolled
                ? "scale-[1.8] md:scale-[2.0] lg:scale-[2.2]"
                : "scale-[2.1] md:scale-[2.4] lg:scale-[2.7]"
            }`}
            onError={() => setLogoError(true)}
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1 pl-2 pr-1">
          {navItems.map((n) => (
            <button
              key={n.id}
              data-testid={`desktop-nav-${n.id}`}
              aria-current={active === n.id ? "page" : undefined}
              onClick={() => onNavClick(n.id)}
              className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                active === n.id
                  ? "text-[#28D8FF]"
                  : "text-slate-300 hover:text-white hover:bg-white/5"
              }`}
            >
              {active === n.id ? (
                <span className="absolute -bottom-0.5 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-[#28D8FF]" aria-hidden />
              ) : null}
              {n.label}
            </button>
          ))}
        </nav>

        {/* Mobile menu toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="ml-auto md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white hover:bg-white/10 pointer-events-auto relative z-[60] transition-colors"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menü öffnen"
          aria-expanded={mobileOpen ? "true" : "false"}
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <>
            <div
              className="fixed inset-0 z-30 bg-[#0A111F]/60 backdrop-blur-sm md:hidden animate-in fade-in duration-200"
              onClick={() => setMobileOpen(false)}
              aria-hidden
            />
            <div className="fixed top-20 left-3 right-3 z-40 md:hidden animate-in fade-in slide-in-from-top-3 duration-300">
              <div className="overflow-hidden rounded-3xl border border-white/10 bg-[#0A111F]/95 shadow-[0_24px_60px_rgba(2,6,23,0.6)] backdrop-blur-xl">
                <nav className="flex flex-col p-3">
                  {navItems.map((n, i) => {
                    const isActive = active === n.id;
                    return (
                      <button
                        key={n.id}
                        data-testid={`mobile-nav-${n.id}`}
                        onClick={() => { setMobileOpen(false); onNavClick(n.id); }}
                        style={{ animationDelay: `${i * 55}ms` }}
                        className={`group relative flex items-center justify-between rounded-2xl px-4 py-3.5 text-left text-base font-medium animate-in fade-in slide-in-from-top-1 fill-mode-both transition-colors ${
                          isActive ? "bg-white/10 text-[#28D8FF]" : "text-slate-200 hover:bg-white/5 hover:text-white"
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          <span className="font-mono text-[11px] text-slate-500">0{i + 1}</span>
                          {n.label}
                        </span>
                        <ArrowRight size={16} className={`transition-transform group-hover:translate-x-0.5 ${isActive ? "text-[#28D8FF]" : "text-slate-500"}`} />
                      </button>
                    );
                  })}
                  <div className="mt-2 px-1 pb-1">
                    <Button
                      data-testid="mobile-nav-cta"
                      onClick={() => { setMobileOpen(false); onNavClick("kontakt"); }}
                      className="w-full rounded-2xl py-6 text-base font-semibold shadow-sm transition-shadow hover:shadow-[0_0_22px_rgba(40,216,255,0.45)]"
                      style={{ backgroundColor: brand.colors.cyan, color: "#063247" }}
                    >
                      Kontakt aufnehmen
                    </Button>
                  </div>
                </nav>
              </div>
            </div>
          </>
        )}

        <div className="ml-auto hidden md:block">
          <Button
            data-testid="header-cta"
            onClick={() => onNavClick("kontakt")}
            className="group rounded-full text-sm font-semibold px-6 shadow-[0_0_15px_rgba(40,216,255,0.15)] transition-shadow hover:shadow-[0_0_25px_rgba(40,216,255,0.45)]"
            style={{ backgroundColor: brand.colors.cyan, color: "#063247" }}
          >
            Kontakt aufnehmen
            <ArrowRight size={15} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
